import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, HIGHWAY_TRUCK, COMPANY } from "@/lib/constants";

const ERROR_MESSAGES = {
  invalid_sso: "That single sign-on link is invalid or has expired. Please sign in with your Apex TMS credentials, or return to apexscs.ca and try again.",
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const errorKey = params.get("error");
  const errorMsg = errorKey && ERROR_MESSAGES[errorKey];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (r.ok) {
      toast.success(`Welcome, ${r.user.name}`);
      nav(r.user.role === "customer" ? "/portal" : "/admin");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative">
        <img src={HIGHWAY_TRUCK} alt="fleet" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-zinc-950/50" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-zinc-100">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Apex" className="h-10 w-10 bg-white p-1" />
            <div>
              <div className="font-bold tracking-tight">{COMPANY.name}</div>
              <div className="mono-label !text-zinc-300">TMS &middot; CONTROL ROOM</div>
            </div>
          </Link>
          <div>
            <div className="mono-label !text-zinc-300">SYSTEM STATUS &middot; ONLINE</div>
            <p className="mt-3 text-3xl font-bold tracking-tight leading-tight max-w-md">
              One login. Every load, BOL, invoice and appointment in one screen.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <Link to="/login" className="flex items-center gap-3 mb-10 lg:hidden">
            <img src={LOGO_URL} alt="Apex" className="h-9 w-9 object-contain" />
            <div>
              <div className="font-bold tracking-tight text-[#0056B3]">APEX</div>
              <div className="mono-label !text-[10px]">TMS control room</div>
            </div>
          </Link>
          <div className="mono-label" data-testid="login-title">SIGN IN &middot; APEX TMS</div>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Welcome back.</h1>
          <p className="mt-2 text-zinc-600 text-sm">Access the dispatch board or your shipper portal.</p>

          {errorMsg && (
            <div
              className="mt-6 border-l-4 border-rose-600 bg-rose-50 px-4 py-3 text-sm text-rose-900"
              data-testid={`login-error-${errorKey}`}
            >
              {errorMsg}
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mono-label">Email</label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="sharp mt-2 apex-input h-12" data-testid="login-email" />
            </div>
            <div>
              <label className="mono-label">Password</label>
              <Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="sharp mt-2 apex-input h-12" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy}
              className="sharp bg-[#0056B3] hover:bg-[#004494] w-full h-12" data-testid="login-submit">
              {busy ? "Signing in..." : "Sign in"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <div className="mt-6 text-sm text-zinc-600">
            New shipper? <Link to="/register" className="underline text-[#0056B3]" data-testid="link-register">Create a customer account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
